import { useEffect, useState } from "react";
import { T } from "@/components/T";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { fetchCategories } from "@/lib/api";
import { resolveImage } from "@/lib/images";
import { stripHtml } from "@/lib/html";
import { MandalaMotif } from "@/components/MotifBackground";
import Seo from "@/components/Seo";
import { ArrowUpRight, Layers } from "lucide-react";

const onImgError = (e) => { e.currentTarget.style.opacity = "0"; };
const catImage = (c) => resolveImage({ image: c.image || c.image_url, deity: c.name, name: `${c.deva_name || ""} ${c.name || ""}` });

export default function Categories() {
  const { pick } = useLang();
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchCategories().then(setCats).catch(() => {}).finally(() => setLoading(false)); }, []);

  return (
    <div data-testid="categories-page">
      <Seo path="/categories" title="Categories — Dharmartha" description="Browse spiritual knowledge by category — deities, festivals, daily practice and more." />
      <section className="relative overflow-hidden border-b border-line bg-[var(--parchment)]">
        <MandalaMotif className="pointer-events-none absolute -right-24 -top-16 hidden h-[460px] w-[460px] text-[var(--gold)] opacity-[0.07] md:block" />
        <div className="relative z-[2] mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
          <p className="overline-label text-gold">{pick("श्रेणियाँ", "Categories")}</p>
          <h1 className="mt-3 font-deva text-4xl leading-tight text-ink deva-lh md:text-6xl" data-testid="page-hero-title">श्रेणियाँ</h1>
          <p className="mt-3 font-serif-latin text-xl text-ink-2 md:text-2xl"><T>Browse by theme</T></p>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-ink-2 md:text-lg">
            {pick("प्रत्येक श्रेणी — उससे जुड़े मंत्र, कथा, स्तोत्र एवं पूजा विधि, एक ही स्थान पर।",
              "Each category gathers its related mantras, kathas, stotras and puja vidhis in one place.")}
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-14 md:px-8 md:py-20">
        {loading ? (
          <p className="py-20 text-center text-ink-3">…</p>
        ) : cats.length === 0 ? (
          <p className="py-20 text-center text-ink-3">{pick("शीघ्र आ रहा है।", "Coming soon.")}</p>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {cats.map((c, i) => (
              <motion.div key={c.slug}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}>
                <Link to={`/categories/${c.slug}`} data-testid={`category-card-${c.slug}`}
                  className="group flex h-full flex-col overflow-hidden border border-line bg-surface transition-all duration-300 hover:-translate-y-1 hover:border-[var(--gold)] hover:shadow-lg">
                  <div className="relative h-48 overflow-hidden bg-[var(--surface-alt)]">
                    <img src={catImage(c)} alt={c.name} loading="lazy" onError={onImgError}
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/5 to-transparent" />
                    {c.deva_name && <p className="absolute bottom-4 left-5 font-deva text-3xl text-white deva-lh">{c.deva_name}</p>}
                    <span className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full border border-white/30 bg-black/30 px-2.5 py-1 text-xs font-medium text-white backdrop-blur-sm">
                      <Layers className="h-3 w-3" /> {c.count}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-serif-latin text-xl text-ink">{c.name}</h3>
                    {(c.description || c.description_hi) && <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-2 line-clamp-3">{stripHtml(pick(c.description_hi, c.description))}</p>}
                    <span className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-saffron opacity-0 transition-opacity group-hover:opacity-100">
                      {pick("देखें", "Explore")} <ArrowUpRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
